import type { IItem } from "@fluidattacks/design";
import { ComboBox } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";

import { StyledWrapper } from "./styles";

import {
  updateFindingFilterStore,
  useGetFindingFiltersStore,
} from "../hooks/use-finding-filters-store";
import type { FindingStatus, VulnerabilityOrigin } from "gql/graphql";

const FindingHighlightedFilters = ({
  groupName,
}: {
  groupName: string;
}): JSX.Element => {
  const { origin, state, severityRating } =
    useGetFindingFiltersStore(groupName);

  const origins = useMemo(
    (): IItem[] => [
      { name: "Inherited", value: "INHERITED" },
      { name: "Injected", value: "INJECTED" },
    ],
    [],
  );

  const states = useMemo(
    (): IItem[] => [
      { name: "Open", value: "VULNERABLE" },
      { name: "Closed", value: "SAFE" },
    ],
    [],
  );

  const severityRatings = useMemo(
    (): IItem[] => [
      { name: "Critical", value: "CRITICAL" },
      { name: "High", value: "HIGH" },
      { name: "Medium", value: "MEDIUM" },
      { name: "Low", value: "LOW" },
    ],
    [],
  );

  const onOriginChange = useCallback(
    (value: string): void => {
      if (origins.map((item): string => item.value).includes(value)) {
        updateFindingFilterStore(groupName, {
          origin: value as VulnerabilityOrigin,
        });
      }
    },
    [origins, groupName],
  );

  const onStateChange = useCallback(
    (value: string): void => {
      if (states.map((item): string => item.value).includes(value)) {
        updateFindingFilterStore(groupName, {
          state: [value as FindingStatus],
        });
      }
    },
    [states, groupName],
  );

  const onSeverityChange = useCallback(
    (value: string): void => {
      if (severityRatings.map((item): string => item.value).includes(value)) {
        updateFindingFilterStore(groupName, {
          severityRating: [value],
        });
      }
    },
    [severityRatings, groupName],
  );

  return (
    <StyledWrapper>
      <ComboBox
        items={origins}
        name={"origin"}
        onChange={onOriginChange}
        placeholder={"Origin"}
        value={origin?.[0] ?? ""}
      />
      <ComboBox
        items={states}
        name={"state"}
        onChange={onStateChange}
        placeholder={"Status"}
        value={state?.[0] ?? ""}
      />
      <ComboBox
        items={severityRatings}
        name={"severityRating"}
        onChange={onSeverityChange}
        placeholder={"Severity"}
        value={severityRating?.[0] ?? ""}
      />
    </StyledWrapper>
  );
};

export { FindingHighlightedFilters };
